import { useEffect, useState } from "react";
import { Sparkles, Clock, CheckCircle2, XCircle, ShieldCheck, Inbox } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Helper activity list — shows a helper (therapist / school staff /
 * grandparent / doctor) the proposals THEY have submitted across all
 * profiles they support, with current decision status.
 *
 * Owner-side review of incoming proposals is handled by
 * <PendingProposalsInbox /> (different component, different audience).
 */
const STATUS_META = {
  pending_clinical: {
    label: "Awaiting clinical review",
    Icon: Clock,
    color: "bg-[color:var(--gold)]",
  },
  pending_parental: {
    label: "Awaiting parent approval",
    Icon: Clock,
    color: "bg-[color:var(--gold)]",
  },
  approved: {
    label: "Approved · live on the board",
    Icon: CheckCircle2,
    color: "bg-emerald-100",
  },
  rejected_clinical: {
    label: "Rejected by clinical reviewer",
    Icon: XCircle,
    color: "bg-rose-100",
  },
  rejected_parental: {
    label: "Rejected by parent",
    Icon: XCircle,
    color: "bg-rose-100",
  },
  withdrawn: {
    label: "Withdrawn",
    Icon: XCircle,
    color: "bg-[color:var(--paper-muted)]",
  },
};

function summarize(action, payload, target) {
  const label = (payload || {}).label || target || "—";
  if (action === "create") return `Add tile "${label}"`;
  if (action === "update") return `Edit tile "${label}"`;
  if (action === "delete") return `Remove tile "${label}"`;
  return action;
}

export default function MyProposalsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    setLoading(true);
    try {
      const { data } = await api.get("/users/me/my-proposals");
      setItems(data?.items || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="brutal-card p-4 bg-[color:var(--paper-muted)] text-sm text-[color:var(--ink-soft)]">
        Loading your proposals…
      </div>
    );
  }

  if (error) {
    return (
      <div className="brutal-card p-4 bg-rose-50 text-sm text-[color:var(--coral-deep)]" data-testid="my-proposals-error">
        {error}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="brutal-card p-4 bg-[color:var(--paper-muted)] text-sm text-[color:var(--ink-soft)] flex items-center gap-2" data-testid="my-proposals-empty">
        <Inbox size={16} />
        You haven't suggested any tile changes yet. When you do, they'll show up here with their status.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="my-proposals-list">
      {items.map((p) => {
        const meta = STATUS_META[p.status] || {
          label: p.status,
          Icon: Sparkles,
          color: "bg-[color:var(--paper-muted)]",
        };
        const Icon = meta.Icon;
        return (
          <div key={p.id} className={`brutal-card p-3 ${meta.color}`} data-testid={`my-proposal-${p.id}`}>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="display text-xs uppercase tracking-[0.2em] font-bold text-[color:var(--coral-deep)]">
                  {p.profile_name || "Profile"}
                </div>
                <div className="text-sm font-semibold mt-0.5">
                  {summarize(p.action, p.payload, p.target_tile_id)}
                </div>
                {p.note && (
                  <div className="text-xs text-[color:var(--ink-soft)] italic mt-1">"{p.note}"</div>
                )}
                {p.clinical_decided_at && (
                  <div className="text-[11px] text-[color:var(--ink-soft)] mt-1 flex items-center gap-1">
                    <ShieldCheck size={11} />
                    Clinically reviewed {new Date(p.clinical_decided_at).toLocaleString()}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 text-xs font-bold whitespace-nowrap">
                <Icon size={14} />
                {meta.label}
              </div>
            </div>
            <div className="text-[11px] text-[color:var(--ink-soft)] mt-2">
              Submitted {new Date(p.created_at).toLocaleString()}
            </div>
          </div>
        );
      })}
    </div>
  );
}
